/**
 * "Origin Not Allowed" error screen.
 *
 * Full-screen overlay shown when the gateway closes the WebSocket with
 * code 1008 and reason "origin not allowed". This happens when the app's
 * origin isn't in the gateway's `allowedOrigins` configuration.
 *
 * Displays the problem, the user's current origin, a ready-to-copy
 * YAML config snippet, and retry/disconnect actions.
 */

import { useState, useCallback, useMemo } from 'react';
import styles from './OriginBlockedOverlay.module.css';

interface OriginBlockedOverlayProps {
  /** Display name of the gateway that rejected the connection */
  gatewayName: string;
  /** Called when the user taps "Retry" to attempt reconnection */
  onRetry: () => void;
  /** Called when the user taps "Disconnect" to remove/abandon the gateway */
  onDisconnect: () => void;
}

/**
 * Origin blocked overlay component.
 *
 * Shows a clear explanation of why the connection was rejected and
 * provides the exact YAML config the user needs to add.
 */
export function OriginBlockedOverlay({
  gatewayName,
  onRetry,
  onDisconnect,
}: OriginBlockedOverlayProps) {
  const [copied, setCopied] = useState(false);
  const origin = window.location.origin;

  const configSnippet = useMemo(
    () =>
      `gateway:\n  controlUi:\n    allowedOrigins:\n      - "${origin}"`,
    [origin],
  );

  const copyConfig = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(configSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may not be available */
    }
  }, [configSnippet]);

  return (
    <div className={styles.overlay}>
      <div className={styles.icon}>🚫</div>
      <h2 className={styles.title}>Origin Not Allowed</h2>
      <p className={styles.description}>
        The gateway <strong>{gatewayName}</strong> doesn't accept connections
        from this app's origin.
      </p>

      <div className={styles.originBadge}>
        <span className={styles.originLabel}>Your origin:</span>
        <code className={styles.originValue}>{origin}</code>
      </div>

      <p className={styles.description}>
        Add this to your OpenClaw config:
      </p>

      <div className={styles.configBox}>
        <pre className={styles.config}>{configSnippet}</pre>
        <button className={styles.copyBtn} onClick={copyConfig}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      <div className={styles.actions}>
        <button className={styles.retryBtn} onClick={onRetry}>
          Retry
        </button>
        <button className={styles.disconnectBtn} onClick={onDisconnect}>
          Disconnect
        </button>
      </div>
    </div>
  );
}
